/* eslint-disable @typescript-eslint/no-floating-promises */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, usePathname } from "next/navigation";
import { useAuth } from "@clerk/nextjs";
import type { RootState } from "@store/index";
import {
  appendMessage,
  createNewTab,
  finalizeConversationTab,
  hydrateConversations,
  renameTab,
  setActiveTab,
  setConversationId,
  setError,
  setStreamingState,
  updateAssistantThought,
  updateAssistantData,
  updateContextChips,
  type AgentChatSession,
} from "@store/features/agentChat/agentChatSlice";
import type {
  ChatMessageThought,
  ChatContextChip,
  DataBlock,
} from "skedyul-ui/ui";
import type { ChatTab, ChatMessage as GlobalChatMessage } from "skedyul-ui/ui";
import { useLocalState } from "skedyul-ui/hooks";
import type { AgentOverrides } from "skedyul-core/features/agents";

export type AgentInput = {
  key: string;
  label?: string;
  description?: string;
  required?: boolean;
  type?: "text" | "number" | "select" | "textarea";
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
};

export type AgentVersionOption = {
  id: string;
  version: number;
  label: string;
  isPublished: boolean;
};

type AgentChatContextValue = {
  tabs: ChatTab[];
  activeTabId: string;
  setActiveTabId: (tabId: string) => void;
  createTab: () => void;
  messages: AgentChatSession["messages"];
  contextChips: ChatContextChip[];
  inputValue: string;
  setInputValue: (value: string) => void;
  handleSubmit: (prompt: string) => void;
  agentName: string | undefined;
  isTestMode: boolean;
  agentInputs: AgentInput[];
  threadInputs: Record<string, unknown>;
  getMissingInputs: () => AgentInput[];
  handleInputsSubmit: (inputs: Record<string, unknown>) => void;
  isStreaming: boolean;
  versions: AgentVersionOption[];
  selectedVersionId: string | null;
  setSelectedVersionId: (versionId: string | null) => void;
};

const AgentChatContext = createContext<AgentChatContextValue | undefined>(
  undefined,
);

export const AgentChatProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const pathname = usePathname();
  const params = useParams<{ subdomain: string | undefined }>();
  const { isLoaded: isAuthLoaded, isSignedIn } = useAuth();
  const subdomain = params.subdomain;
  const tabs = useSelector((state: RootState) => state.agentChat.tabs);
  const activeTabId = useSelector(
    (state: RootState) => state.agentChat.activeTabId,
  );
  const sessions = useSelector((state: RootState) => state.agentChat.sessions);
  const session = useMemo(() => sessions[activeTabId], [sessions, activeTabId]);

  const agentEditor = useSelector((state: RootState) => state.agents.editor);
  const editorInputs = useSelector(
    (state: RootState) => state.agents.editor.working?.inputs,
  );
  const currentAgentData = useSelector(
    (state: RootState) => state.agents.currentAgent.data,
  );

  const isOnAgentPage = useMemo(() => {
    return (
      pathname?.includes("/settings/agents/") &&
      !pathname?.endsWith("/agents") &&
      !pathname?.endsWith("/agents/")
    );
  }, [pathname]);

  const agentContext = useMemo(() => {
    if (!isOnAgentPage || !agentEditor.working) {
      return {
        agentId: undefined,
        agentOverrides: undefined,
        agentName: undefined,
        hasChanges: false,
      };
    }

    const hasChanges =
      agentEditor.original &&
      agentEditor.working &&
      (agentEditor.original.name !== agentEditor.working.name ||
        agentEditor.original.description !== agentEditor.working.description ||
        agentEditor.original.system !== agentEditor.working.system ||
        agentEditor.original.llmModelId !== agentEditor.working.llmModelId);

    const agentId = currentAgentData?.id ?? agentEditor.working.id;
    const agentName = agentEditor.working.name;

    const agentOverrides: AgentOverrides | undefined = hasChanges
      ? {
          systemPrompt: agentEditor.working.system,
          llmModelId: agentEditor.working.llmModelId,
        }
      : undefined;

    return {
      agentId,
      agentOverrides,
      agentName,
      hasChanges: hasChanges ?? false,
    };
  }, [isOnAgentPage, agentEditor, currentAgentData]);

  const [persistedActiveTabId, setPersistedActiveTabId] = useLocalState<
    string | null
  >("agentChat:lastTabId", () => null);
  const initialPersistedTabIdRef = useRef<string | null>(persistedActiveTabId);

  const [inputValue, setInputValue] = useState("");
  const [agentInputs, setAgentInputs] = useState<AgentInput[]>([]);
  const [threadInputs, setThreadInputs] = useState<Record<string, unknown>>({});
  const [versions, setVersions] = useState<AgentVersionOption[]>([]);
  const [localSelectedVersionId, setLocalSelectedVersionId] = useState<
    string | null
  >(null);

  // Use editor's selected version when on agent page, otherwise use local state
  const selectedVersionId =
    isOnAgentPage && agentEditor.selectedVersionId
      ? agentEditor.selectedVersionId
      : localSelectedVersionId;

  const setSelectedVersionId = (versionId: string | null) => {
    setLocalSelectedVersionId(versionId);
  };

  const messages = session?.messages ?? [];
  const contextChips = session?.contextChips ?? [];
  const conversationId = session?.conversationId;
  const isStreaming = session?.isStreaming ?? false;

  // Calculate missing inputs based on required agent inputs and existing thread inputs
  const getMissingInputs = useCallback((): AgentInput[] => {
    return agentInputs.filter((input) => {
      if (!input.required) return false;
      const value = threadInputs[input.key];
      const hasValue = value !== undefined && value !== null && value !== "";
      return !hasValue;
    });
  }, [agentInputs, threadInputs]);

  // Reset thread inputs when switching tabs or agents
  useEffect(() => {
    setThreadInputs({});
  }, [activeTabId, agentContext.agentId]);

  // Load agent versions when on agent page - sync with editor versions
  useEffect(() => {
    if (!isOnAgentPage || !agentContext.agentId) {
      setVersions([]);
      setLocalSelectedVersionId(null);
      return;
    }

    // Use editor versions if available (they're already loaded by the page)
    if (agentEditor.versions && agentEditor.versions.length > 0) {
      setVersions(
        agentEditor.versions.map((v) => ({
          id: v.id,
          version: v.version ?? 0,
          label: v.versionLabel || `v${v.version}`,
          isPublished: v.isPublished,
        })),
      );
      return;
    }

    // Fallback: load versions from API if editor doesn't have them
    const loadVersions = async () => {
      try {
        const response = await fetch(
          `/api/agents/${agentContext.agentId}/versions`,
        );
        if (response.ok) {
          const data = (await response.json()) as {
            versions: AgentVersionOption[];
          };
          setVersions(data.versions || []);
          // Default to published version only if no editor selection
          const publishedVersion = data.versions?.find((v) => v.isPublished);
          if (
            publishedVersion &&
            !agentEditor.selectedVersionId &&
            !localSelectedVersionId
          ) {
            setLocalSelectedVersionId(publishedVersion.id);
          }
        } else {
          setVersions([]);
        }
      } catch (error) {
        console.error("Failed to load agent versions:", error);
        setVersions([]);
      }
    };

    loadVersions();
  }, [
    isOnAgentPage,
    agentContext.agentId,
    agentEditor.versions,
    agentEditor.selectedVersionId,
  ]);

  // Load agent inputs when on agent page - prefer editor's working state for sandbox testing
  useEffect(() => {
    if (!isOnAgentPage || !agentContext.agentId) {
      setAgentInputs([]);
      return;
    }

    // When on agent page, prefer the editor's working inputs (for sandbox testing unsaved changes)
    if (editorInputs && Object.keys(editorInputs).length > 0) {
      const inputs: AgentInput[] = Object.entries(editorInputs).map(
        ([key, value]) => ({
          key,
          label: key,
          description: value.description,
          required: value.required ?? true,
          type: "text" as const,
        }),
      );
      setAgentInputs(inputs);
      return;
    }

    // Fallback to API for saved inputs
    const loadAgentInputs = async () => {
      try {
        // Build URL with optional versionId parameter
        const url = selectedVersionId
          ? `/api/agents/${agentContext.agentId}/inputs?versionId=${selectedVersionId}`
          : `/api/agents/${agentContext.agentId}/inputs`;

        const response = await fetch(url);
        if (response.ok) {
          const data = (await response.json()) as { inputs: AgentInput[] };
          setAgentInputs(data.inputs || []);
        } else {
          setAgentInputs([]);
        }
      } catch (error) {
        console.error("Failed to load agent inputs:", error);
        setAgentInputs([]);
      }
    };

    loadAgentInputs();
  }, [isOnAgentPage, agentContext.agentId, selectedVersionId, editorInputs]);

  // Load thread inputs when conversationId changes (existing thread)
  useEffect(() => {
    if (!conversationId) {
      // New conversation - thread inputs will be empty until submitted
      return;
    }

    const loadThreadInputs = async () => {
      try {
        const response = await fetch(`/api/threads/${conversationId}/inputs`);
        if (response.ok) {
          const data = (await response.json()) as {
            inputs: Record<string, unknown>;
          };
          setThreadInputs(data.inputs || {});
        }
      } catch (error) {
        console.error("Failed to load thread inputs:", error);
      }
    };

    loadThreadInputs();
  }, [conversationId]);

  useEffect(() => {
    if (!isAuthLoaded || !isSignedIn || !subdomain) {
      return;
    }

    let cancelled = false;
    const loadConversations = async () => {
      try {
        const response = await fetch(
          `/api/agents/conversations?limit=5&subdomain=${encodeURIComponent(subdomain)}`,
          {
            cache: "no-store",
          },
        );
        if (!response.ok) {
          return;
        }
        const data = (await response.json()) as {
          conversations: Array<{
            id: string;
            title: string;
            messages: GlobalChatMessage[];
            contextChips?: ChatContextChip[];
          }>;
        };
        if (cancelled) {
          return;
        }

        if (data.conversations.length > 0) {
          dispatch(hydrateConversations(data.conversations));

          const availableIds = new Set(
            data.conversations.map((conversation) => conversation.id),
          );

          const preferredId =
            initialPersistedTabIdRef.current &&
            availableIds.has(initialPersistedTabIdRef.current)
              ? initialPersistedTabIdRef.current
              : data.conversations[0]?.id;

          if (preferredId) {
            dispatch(setActiveTab(preferredId));
            setPersistedActiveTabId(preferredId);
          }
        }
      } catch (error) {
        console.error("Failed to hydrate agent conversations", error);
      }
    };

    void loadConversations();
    return () => {
      cancelled = true;
    };
  }, [dispatch, isAuthLoaded, isSignedIn, setPersistedActiveTabId, subdomain]);

  useEffect(() => {
    if (activeTabId) {
      setPersistedActiveTabId(activeTabId);
    }
  }, [activeTabId, setPersistedActiveTabId]);

  const setActiveTabId = useCallback(
    (tabId: string) => {
      dispatch(setActiveTab(tabId));
      setInputValue("");
    },
    [dispatch],
  );

  const createTab = useCallback(() => {
    dispatch(createNewTab({ makeActive: true }));
    setInputValue("");
    setThreadInputs({});
  }, [dispatch]);

  const handleInputsSubmit = useCallback(
    async (inputs: Record<string, unknown>) => {
      // Merge new inputs with existing thread inputs
      const mergedInputs = { ...threadInputs, ...inputs };
      setThreadInputs(mergedInputs);

      if (conversationId) {
        try {
          await fetch(`/api/threads/${conversationId}/inputs`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ inputs }),
          });
        } catch (error) {
          console.error("Failed to save thread inputs:", error);
        }
      }
    },
    [conversationId, threadInputs],
  );

  const appendAssistantErrorMessage = useCallback(
    (tabId: string, text: string) => {
      dispatch(
        appendMessage({
          tabId,
          message: {
            id: `assistant-error-${Date.now().toString()}`,
            author: "assistant",
            sender: "Ops copilot",
            timestamp: "Just now",
            content: text,
          },
        }),
      );
    },
    [dispatch],
  );

  const handleSubmit = useCallback(
    async (rawPrompt: string) => {
      const prompt = rawPrompt.trim();
      if (!prompt) return;

      const timestampLabel = "Just now";
      let hasConversationId = Boolean(conversationId);

      dispatch(
        appendMessage({
          tabId: activeTabId,
          message: {
            id: `user-${Date.now().toString()}`,
            author: "user",
            sender: "You",
            timestamp: timestampLabel,
            content: prompt,
          },
        }),
      );

      setInputValue("");
      dispatch(
        setStreamingState({
          tabId: activeTabId,
          isStreaming: true,
        }),
      );
      dispatch(
        setError({
          tabId: activeTabId,
          error: null,
        }),
      );

      let targetTabId = activeTabId;

      try {
        const response = await fetch("/api/agents/chat", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            prompt,
            tabId: targetTabId,
            threadId: conversationId ?? undefined,
            agentId: agentContext.agentId,
            agentVersionId: selectedVersionId ?? undefined,
            agentOverrides: agentContext.agentOverrides,
          }),
        });

        if (!response.ok || !response.body) {
          throw new Error("Failed to start agent stream");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let thoughtStartTime: number | null = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const jsonPart = line.slice(6);

            try {
              const data = JSON.parse(jsonPart) as {
                conversationId?: string;
                conversationTitle?: string;
                message?: string;
                thought?: string;
                thoughtComplete?: boolean;
                thoughts?: ChatMessageThought[];
                data?: DataBlock[];
                context?: ChatContextChip[];
                done?: boolean;
                initial?: boolean;
                error?: string;
              };

              if (data.error) {
                throw new Error(data.error);
              }

              if (data.conversationId && !hasConversationId) {
                dispatch(
                  finalizeConversationTab({
                    tempId: targetTabId,
                    conversationId: data.conversationId,
                    title: data.conversationTitle,
                  }),
                );
                dispatch(
                  setConversationId({
                    tabId: data.conversationId,
                    conversationId: data.conversationId,
                  }),
                );
                hasConversationId = true;
                targetTabId = data.conversationId;
              }

              if (data.conversationTitle) {
                dispatch(
                  renameTab({
                    tabId: targetTabId,
                    title: data.conversationTitle,
                    isDraft: false,
                  }),
                );
              }

              if (typeof data.thought === "string") {
                if (!thoughtStartTime) {
                  thoughtStartTime = Date.now();
                }

                if (data.thoughtComplete) {
                  const durationMs = Date.now() - thoughtStartTime;
                  dispatch(
                    updateAssistantThought({
                      tabId: targetTabId,
                      thoughtContent: data.thought,
                      isThinking: false,
                      durationMs,
                    }),
                  );
                  thoughtStartTime = null;
                } else {
                  dispatch(
                    updateAssistantThought({
                      tabId: targetTabId,
                      thoughtContent: data.thought,
                      isThinking: true,
                    }),
                  );
                }
              }

              if (
                data.data &&
                Array.isArray(data.data) &&
                data.data.length > 0
              ) {
                dispatch(
                  updateAssistantData({
                    tabId: targetTabId,
                    data: data.data,
                  }),
                );
              }

              if (
                data.context &&
                Array.isArray(data.context) &&
                data.context.length > 0
              ) {
                dispatch(
                  updateContextChips({
                    tabId: targetTabId,
                    contextChips: data.context,
                  }),
                );
              }

              if (typeof data.message === "string") {
                dispatch({
                  type: "agentChat/updateAssistantMessage",
                  payload: {
                    tabId: targetTabId,
                    content: data.message,
                    thoughts: data.thoughts,
                    data: data.data,
                  },
                });
              }

              if (data.done) {
                dispatch(
                  setStreamingState({
                    tabId: targetTabId,
                    isStreaming: false,
                  }),
                );
              }
            } catch (error) {
              console.error("Error parsing agent stream data:", error);
              dispatch(
                setError({
                  tabId: targetTabId,
                  error:
                    error instanceof Error ? error.message : "Stream error",
                }),
              );
              dispatch(
                setStreamingState({
                  tabId: targetTabId,
                  isStreaming: false,
                }),
              );
            }
          }
        }
      } catch (error) {
        console.error("Agent chat stream failed:", error);
        const fallbackText =
          "I couldn't reach the agent services. Please try again in a moment.";
        appendAssistantErrorMessage(targetTabId, fallbackText);
        dispatch(
          setError({
            tabId: targetTabId,
            error:
              error instanceof Error ? error.message : "Agent stream failed",
          }),
        );
        dispatch(
          setStreamingState({
            tabId: targetTabId,
            isStreaming: false,
          }),
        );
      }
    },
    [
      activeTabId,
      appendAssistantErrorMessage,
      conversationId,
      dispatch,
      agentContext,
      selectedVersionId,
    ],
  );

  const value: AgentChatContextValue = {
    tabs,
    activeTabId,
    setActiveTabId,
    createTab,
    messages,
    contextChips,
    inputValue,
    setInputValue,
    handleSubmit,
    agentName: agentContext.agentName,
    isTestMode: agentContext.hasChanges,
    agentInputs,
    threadInputs,
    getMissingInputs,
    handleInputsSubmit,
    isStreaming,
    versions,
    selectedVersionId,
    setSelectedVersionId,
  };

  return (
    <AgentChatContext.Provider value={value}>
      {children}
    </AgentChatContext.Provider>
  );
};

export const useAgentChat = (): AgentChatContextValue => {
  const ctx = useContext(AgentChatContext);
  if (!ctx) {
    throw new Error("useAgentChat must be used within AgentChatProvider");
  }
  return ctx;
};
